import React, { useMemo, useState, useEffect } from "react";
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    LinearProgress,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogContentText,
    DialogActions,
    Button,
} from "@mui/material";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PublicIcon from "@mui/icons-material/Public";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import LaunchIcon from "@mui/icons-material/Launch";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AutoGraphIcon from "@mui/icons-material/AutoGraph"; // 🎯 Icono matemático para ruteo inteligente

import RouteMap from "../../../administracion/rutas/RouteMap";
import PassengerLocationMap from './PassengerLocationMap';
import PassengerSurveyCell from './PassengerSurveyCell';
//import VrpOptimizationDialog from "./VrpOptimizationDialog";
import VrpIterativeDialog from './VrpIterativeDialog';
import { useTaxiServicesTypes } from "../../../../hooks/administration/useTaxiServicesTypes";
import { usePersonForm } from "../../../../hooks/usePersons";
import { useTaxiWorkflow } from "../../../../hooks/taxi/useTaxiWorkflow";
import { useSurveyWorkflow } from "../../../../hooks/administration/useSurveyWorkflow";

const getEstadoColor = (estado) => {
    if (estado === "VIAJO") return "success";
    if (estado === "NO_VIAJO") return "error";
    if (estado === "RESERVADO") return "warning";
    if (estado === "NO_ASIGNADO_A_MANIFIESTO") return "default";
    return "default";
};

const getEstadoOrdenColor = (estado) => {
    if (estado === "ASIGNADO") return "success";
    if (estado === "NO_ASIGNADO") return "error";
    if (estado === "INVITADO") return "info";
    return "default";
};

const groupByManifest = (travelers = []) => {
    const result = {
        manifests: {},
        noTravelers: [],
    };

    travelers.forEach((t) => {
        const isNoTravel =
            t.estadoreporte === "NO_VIAJO" ||
            (!t.manid && t.ordserdetestpas === "NO_ASIGNADO");

        if (isNoTravel) {
            result.noTravelers.push(t);
            return;
        }

        const key = t.manid || "SIN_MANIFIESTO";

        if (!result.manifests[key]) {
            result.manifests[key] = {
                manid: t.manid,
                maninf: t.maninf,
                manest: t.manest,
                vehid: t.vehid || t.VehID || t.vehicleId || null,
                whatsappsent: t.whatsappsent ?? t.WhatsappSent ?? false,
                travelers: [],
            };
        }

        result.manifests[key].travelers.push(t);
    });

    return result;
};

// =========================================================================
// SUB-COMPONENTE: TABLA OPERATIVA INTERNA (MINI TABLE)
// =========================================================================
const TravelersMiniTable = ({
    travelers = [],
    paradas = [],
    canEdit,
    serviceId,
    user,
    updateTraveler,
    onDelete,
    showAlert,
    refetchTravelers,
    loading,
    permissions = {},
    isTaxiService = false,
    showSurveyColumn = false,
    manifestId = null,
    onRetrySurvey,
    isRetryingSurvey = false,
    surveyRetryVersion = 0,
}) => {
    const canShowTaxi = permissions.canViewTaxi !== false;
    const canShowInternalStatus = permissions.canViewInternalStatus !== false;
    const canShowActions = permissions.canViewActions !== false && canEdit;

    const [editingRowId, setEditingRowId] = useState(null);
    const [rowDraft, setRowDraft] = useState({});

    const [activePersonId, setActivePersonId] = useState(null);
    const [currentPersonLocations, setCurrentPersonLocations] = useState([]);

    const { personDetail, isLoadingDetail } = usePersonForm(activePersonId);
    const { taxiServicesTypes: taxiTypes } = useTaxiServicesTypes();

    useEffect(() => {
        if (personDetail?.direcciones) {
            setCurrentPersonLocations(personDetail.direcciones);

            setRowDraft((prev) => {
                if (prev.ordserdettaxser && !prev.taxlocid && personDetail.direcciones.length > 0) {
                    const mainLoc = personDetail.direcciones.find(loc => loc.perlocpri || loc.PerLocPri);
                    return {
                        ...prev,
                        taxlocid: mainLoc ? mainLoc.locid : personDetail.direcciones[0].locid
                    };
                }
                return prev;
            });
        } else {
            setCurrentPersonLocations([]);
        }
    }, [personDetail]);

    const handleEditRow = (traveler) => {
        setEditingRowId(traveler.ordserdetid);

        setRowDraft({
            ordserdettra: traveler.ordserdettra || "IDA",
            ordserdetpunsubid: traveler.ordserdetpunsubid || "",
            ordserdetpunbajid: traveler.ordserdetpunbajid || "",
            ordserdettaxser: isTaxiService ? true : !!traveler.ordserdettaxser,
            ordserdetdes: traveler.ordserdetdes || "",
            taxtipid: traveler.taxtipid || "",
            taxlocid: traveler.taxlocid || "",
        });

        setActivePersonId(traveler.perid || null);
    };

    const handleCancelEdit = () => {
        setEditingRowId(null);
        setRowDraft({});
        setActivePersonId(null);
        setCurrentPersonLocations([]);
    };

    const updateDraft = (field, value) => {
        setRowDraft((prev) => {
            const updated = { ...prev, [field]: value };

            if (field === "ordserdettaxser" && !value) {
                updated.taxtipid = "";
                updated.taxlocid = "";
            }

            if (field === "ordserdettaxser" && value && !updated.taxtipid) {
                updated.taxtipid = 3;
                if (currentPersonLocations.length > 0 && !updated.taxlocid) {
                    const mainLoc = currentPersonLocations.find(loc => loc.perlocpri || loc.PerLocPri);
                    updated.taxlocid = mainLoc ? mainLoc.locid : currentPersonLocations[0].locid;
                }
            }

            return updated;
        });
    };

    const handleSaveRow = (traveler) => {
        if (!rowDraft.ordserdetpunsubid || !rowDraft.ordserdetpunbajid) {
            showAlert("Debe seleccionar punto de subida y punto de bajada.", "error");
            return;
        }

        if (Number(rowDraft.ordserdetpunsubid) === Number(rowDraft.ordserdetpunbajid)) {
            showAlert("El punto de subida y bajada no pueden ser iguales.", "error");
            return;
        }

        updateTraveler.mutate(
            {
                servicioDetalleId: traveler.ordserdetid,
                data: {
                    ...rowDraft,
                    perid: traveler.perid,
                    ordsercabid: serviceId,
                    taxtipid: rowDraft.ordserdettaxser ? Number(rowDraft.taxtipid) : null,
                    taxlocid: rowDraft.ordserdettaxser ? Number(rowDraft.taxlocid) : null,
                    updbyid: user.EmpID,
                },
            },
            {
                onSuccess: () => {
                    showAlert("Viajero actualizado correctamente.");
                    setEditingRowId(null);
                    setRowDraft({});
                    setActivePersonId(null);
                    setCurrentPersonLocations([]);
                    refetchTravelers();
                },
                onError: (error) => {
                    showAlert(error?.response?.data?.error || "Error al actualizar viajero.", "error");
                },
            }
        );
    };

    const colSpan =
        6 +                                         // #, DNI, Pasajero, Subida, Bajada, Estado viaje
        (canShowTaxi && !isTaxiService ? 1 : 0) +   // Taxi
        (canShowTaxi ? 1 : 0) +                     // Destino/Grupo
        (canShowInternalStatus ? 1 : 0) +           // Estado OS
        (showSurveyColumn ? 1 : 0) +                // Encuesta
        (canShowActions ? 1 : 0);                   // Acciones

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ bgcolor: "grey.100" }}>
                        <TableCell width={60}><strong>#</strong></TableCell>
                        <TableCell width={90}><strong>DNI</strong></TableCell>
                        <TableCell width={180}><strong>Pasajero</strong></TableCell>
                        <TableCell width={90}><strong>Punto subida</strong></TableCell>
                        <TableCell width={90}><strong>Punto bajada</strong></TableCell>
                        {canShowTaxi && !isTaxiService && <TableCell align="center" width={70}><strong>Taxi</strong></TableCell>}
                        {canShowTaxi && <TableCell width={220}><strong>Destino / Grupo Taxi</strong></TableCell>}
                        {canShowInternalStatus && <TableCell width={110}><strong>Estado OS</strong></TableCell>}
                        <TableCell width={110}><strong>Estado viaje</strong></TableCell>
                        {canShowActions && <TableCell align="center" width={110}><strong>Acciones</strong></TableCell>}
                        {showSurveyColumn && (<TableCell align="center" width={130}><strong>Encuesta</strong></TableCell>)}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {loading && (
                        <TableRow>
                            <TableCell colSpan={colSpan + 1} align="center"><CircularProgress size={24} /></TableCell>
                        </TableRow>
                    )}

                    {!loading && travelers.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={colSpan + 1} align="center">No hay viajeros.</TableCell>
                        </TableRow>
                    )}

                    {!loading &&
                        travelers.map((t, index) => {
                            const isEditing = editingRowId === t.ordserdetid;
                            const canModifyThisRow = canEdit && t.ordserdetestpas === "NO_ASIGNADO";

                            return (
                                <TableRow
                                    key={t.ordserdetid || `${t.perid}-${index}`}
                                    sx={{
                                        bgcolor:
                                            t.estadoreporte === "VIAJO"
                                                ? "success.50"
                                                : t.estadoreporte === "NO_VIAJO" || t.ordserdetestpas === "NO_ASIGNADO"
                                                    ? "#FFEBEE"
                                                    : "inherit",
                                    }}
                                >
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{t.perdni}</TableCell>
                                    <TableCell><Typography variant="body2" fontWeight={700}>{t.perape} {t.pernom}</Typography></TableCell>

                                    <TableCell sx={{ minWidth: 220 }}>
                                        {isEditing ? (
                                            <TextField
                                                select
                                                size="small"
                                                fullWidth
                                                value={rowDraft.ordserdetpunsubid}
                                                onChange={(e) =>
                                                    updateDraft(
                                                        "ordserdetpunsubid",
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                {paradas.map((p) => (
                                                    <MenuItem key={p.locid} value={p.locid}>
                                                        {p.locnom}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        ) : (
                                            t.puntosubida || "N/A"
                                        )}
                                    </TableCell>

                                    <TableCell sx={{ minWidth: 220 }}>
                                        {isEditing ? (
                                            <TextField
                                                select
                                                size="small"
                                                fullWidth
                                                value={rowDraft.ordserdetpunbajid}
                                                onChange={(e) =>
                                                    updateDraft(
                                                        "ordserdetpunbajid",
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                {paradas.map((p) => (
                                                    <MenuItem key={p.locid} value={p.locid}>
                                                        {p.locnom}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        ) : (
                                            t.puntobajada || "N/A"
                                        )}
                                    </TableCell>

                                    {canShowTaxi && !isTaxiService && (
                                        <TableCell align="center">
                                            {isEditing ? (
                                                <Checkbox checked={!!rowDraft.ordserdettaxser} onChange={(e) => updateDraft("ordserdettaxser", e.target.checked)} />
                                            ) : t.ordserdettaxser ? (
                                                <Chip label="Sí" color="warning" size="small" />
                                            ) : "No"}
                                        </TableCell>
                                    )}

                                    {canShowTaxi && (
                                        <TableCell>
                                            {isEditing ? (
                                                <Box display="flex" flexDirection="column" gap={1} width="100%">
                                                    <TextField
                                                        select
                                                        size="small"
                                                        label={isLoadingDetail ? "Cargando..." : "Domicilio"}
                                                        disabled={isLoadingDetail}
                                                        fullWidth
                                                        value={rowDraft.taxlocid || ""}
                                                        onChange={(e) => updateDraft("taxlocid", e.target.value)}
                                                    >
                                                        <MenuItem value="">Seleccione destino...</MenuItem>
                                                        {currentPersonLocations.map((loc) => (
                                                            <MenuItem key={loc.locid} value={loc.locid}>
                                                                {loc.perloctip}: {loc.locnom}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>

                                                    <TextField
                                                        select
                                                        size="small"
                                                        label="Grupo"
                                                        fullWidth
                                                        value={rowDraft.taxtipid || ""}
                                                        onChange={(e) => updateDraft("taxtipid", e.target.value)}
                                                    >
                                                        <MenuItem value="">Seleccione grupo...</MenuItem>
                                                        {(taxiTypes || []).map((type) => (
                                                            <MenuItem key={type.taxtipid} value={type.taxtipid}>
                                                                {type.taxtipnom}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Box>
                                            ) : (t.ordserdettaxser || isTaxiService) ? (
                                                <Box>
                                                    <Typography variant="caption" fontWeight={700} display="block" color="primary.main">
                                                        🏠 {t.destinonombre || "No configurado"}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ bgcolor: '#fff3e0', color: '#e65100', px: 0.8, py: 0.2, borderRadius: 0.5, fontWeight: 700, mt: 0.5, display: 'inline-block' }}>
                                                        🚖 {t.taxtipnom || "GRUPO_3"}
                                                    </Typography>
                                                </Box>
                                            ) : "-"}
                                        </TableCell>
                                    )}

                                    {canShowInternalStatus && (
                                        <TableCell>
                                            <Chip label={t.ordserdetestpas || "-"} size="small" color={getEstadoOrdenColor(t.ordserdetestpas)} sx={{ fontWeight: 700 }} />
                                        </TableCell>
                                    )}

                                    <TableCell>
                                        <Chip label={t.estadoreporte || "-"} size="small" color={getEstadoColor(t.estadoreporte)} sx={{ fontWeight: 700 }} />
                                    </TableCell>

                                    {canShowActions && (
                                        <TableCell align="center">
                                            {isEditing ? (
                                                <Box display="flex" justifyContent="center">
                                                    <Tooltip title="Guardar">
                                                        <span>
                                                            <IconButton color="primary" onClick={() => handleSaveRow(t)} disabled={updateTraveler.isPending || isLoadingDetail}>
                                                                <SaveIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Cancelar">
                                                        <IconButton onClick={handleCancelEdit}>
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ) : (
                                                <Box display="flex" justifyContent="center">
                                                    <Tooltip title={canModifyThisRow ? "Editar" : "Solo se pueden editar viajeros no asignados"}>
                                                        <span>
                                                            <IconButton color="primary" disabled={!canModifyThisRow} onClick={() => handleEditRow(t)}>
                                                                <EditIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={canModifyThisRow ? "Eliminar" : "Solo se pueden eliminar viajeros no asignados"}>
                                                        <span>
                                                            <IconButton color="error" disabled={!canModifyThisRow} onClick={() => onDelete(t)}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Box>
                                            )}
                                        </TableCell>
                                    )}
                                    {showSurveyColumn && (
                                        <TableCell align="center">
                                            <PassengerSurveyCell
                                                manifestId={manifestId}
                                                passengerId={t.perid}
                                                onRetry={onRetrySurvey}
                                                isRetrying={isRetryingSurvey}
                                                retryVersion={surveyRetryVersion}
                                            />
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// =========================================================================
// COMPONENTE PRINCIPAL: REPORTE DE MANIFIESTOS CON COLLAPSE Y VRP NATIVO
// =========================================================================
const TravelersManifestReport = ({
    title,
    travelers = [],
    paradas = [],
    canEdit,
    serviceId,
    user,
    updateTraveler,
    onDelete,
    showAlert,
    refetchTravelers,
    loading,
    permissions = {},
    service
}) => {
    const grouped = useMemo(() => groupByManifest(travelers), [travelers]);

    const [expandedGroups, setExpandedGroups] = useState({});
    const [openMap, setOpenMap] = useState(false);
    const [mapData, setMapData] = useState(null);

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedManifestData, setSelectedManifestData] = useState(null);
    const [generatedTaxiOSId, setGeneratedTaxiOSId] = useState(null);

    const [openNotifyDialog, setOpenNotifyDialog] = useState(false);

    const [openPassengerMap, setOpenPassengerMap] = useState(false);

    const [openVrpVariantsDialog, setOpenVrpVariantsDialog] = useState(false);
    const [openAutoVrpDialog, setOpenAutoVrpDialog] = useState(false);
    const [openIterativeDialog, setOpenIterativeDialog] = useState(false);

    const canShowTaxiFunctions = !!permissions?.canEnableServiceTaxi;
    const canSendSurveyNotification = !!permissions?.canViewFinancialData

    // 🎯 ADICIÓN 1: Estado para controlar la apertura del Modal de Optimización VRP
    const [openVrpDialog, setOpenVrpDialog] = useState(false);

    const initialVrpProgress = {
        jobId: null,
        status: "IDLE",
        stage: "IDLE",
        progress: 0,
        message: "",
        manifestsCount: 0,
        passengersCount: 0,
        error: null
    };

    const [vrpStarted, setVrpStarted] = useState(false);
    const [vrpProgress, setVrpProgress] = useState(initialVrpProgress);
    const [vrpStreamError, setVrpStreamError] = useState(false);

    const [openSurveyDialog, setOpenSurveyDialog] = useState(false);
    const [surveyManifestId, setSurveyManifestId] = useState(null);
    const [surveyRetryVersion, setSurveyRetryVersion] = useState(0);

    const isTaxiServiceType = useMemo(() => Number(service?.tipserid) === 3, [service]);

    const handleCreationSuccess = (responseData) => {
        if (responseData?.taxiOSID || responseData?.id) {
            setGeneratedTaxiOSId(responseData.taxiOSID || responseData.id);
        }
    };

    const {
        initTaxiMutation,
        notifyInitialMutation,
        generateRoutesMutation,
        subscribeTaxiRoutingJob,
        getTaxiRoutingJob,
        refreshAfterRouting,
        isWorkflowBusy
    } = useTaxiWorkflow(showAlert, refetchTravelers, handleCreationSuccess);

    const {
        notifySurveyMutation,
        retrySurveyMutation,
        isSurveyBusy
    } = useSurveyWorkflow(showAlert, refetchTravelers);

    const handleToggleExpand = (groupKey) => {
        setExpandedGroups((prev) => ({ ...prev, [groupKey]: !(prev[groupKey] ?? false) }));
    };

    const handleOpenMap = (group) => {
        if (!group?.manid) return;
        setMapData({ id: Number(group.manid), vehicleId: group.vehid || null });
        setOpenMap(true);
    };

    const handleCloseMap = () => {
        setOpenMap(false);
        setMapData(null);
    };

    const handleOpenConfirmDialog = (manId, totalPassengers) => {
        setGeneratedTaxiOSId(null);
        setSelectedManifestData({ manId, totalPassengers });
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        if (isWorkflowBusy) return;
        setConfirmDialogOpen(false);
        setSelectedManifestData(null);
        setGeneratedTaxiOSId(null);
    };

    const handleOpenSurveyDialog = (manId) => {
        setSurveyManifestId(manId);
        setOpenSurveyDialog(true);
    };

    const handleCloseSurveyDialog = () => {
        if (notifySurveyMutation.isPending) return;
        setOpenSurveyDialog(false);
        setSurveyManifestId(null);
    };

    const handleExecuteSurveyNotification = () => {
        if (!surveyManifestId) return;

        notifySurveyMutation.mutate(Number(surveyManifestId), {
            onSuccess: () => {
                setOpenSurveyDialog(false);
                setSurveyManifestId(null);
            }
        });
    };

    const handleRetrySurveyForPassenger = (manifestId, perId) => {
        retrySurveyMutation.mutate(
            { manifestId: Number(manifestId), perId: Number(perId) },
            {
                onSuccess: () => {
                    setSurveyRetryVersion(v => v + 1);
                }
            }
        );
    };

    const handleExecuteTaxiInit = () => {
        if (!selectedManifestData) return;
        initTaxiMutation.mutate({
            parentOrderId: Number(serviceId),
            manId: Number(selectedManifestData.manId)
        });
    };

    const handleExecuteInitialNotification = () => {
        notifyInitialMutation.mutate(Number(serviceId), {
            onSuccess: () => {
                setOpenNotifyDialog(false);
            }
        });
    };

    const handleOpenAutoVrp = () => setOpenAutoVrpDialog(true);
    //const handleCloseAutoVrp = () => setOpenAutoVrpDialog(false);

    const handleCloseAutoVrp = () => {
        if (isVrpProcessing) return;
        setOpenAutoVrpDialog(false);
        window.setTimeout(() => {
            resetVrpState();
        }, 200);
    };

    const handleOpenIterative = () => setOpenIterativeDialog(true);
    const handleCloseIterative = () => setOpenIterativeDialog(false);

    const handleVrpPersistSuccess = (data) => {
        showAlert(`Se generaron ${data.manifestsCreated} manifiestos para ${data.totalPassengers} pasajeros.`);
        refetchTravelers();
    };

    const handleVrpPersistError = (error) => {
        showAlert(error?.message || "Error al persistir la variante.", "error");
    };

    const handleExecuteVrpOptimization = () => {
        setVrpStarted(true);
        setVrpStreamError(false);

        setVrpProgress({
            ...initialVrpProgress,
            status: "SUBMITTING",
            stage: "SUBMITTING",
            progress: 0,
            message:
                "Enviando solicitud al backend..."
        });

        generateRoutesMutation.mutate(
            Number(serviceId),
            {
                onSuccess: async response => {
                    const jobId = response?.jobId;

                    if (!jobId) {
                        setVrpProgress(previous => ({
                            ...previous,
                            status: "FAILED",
                            stage: "FAILED",
                            error:
                                "El backend no devolvió el identificador del trabajo.",
                            message:
                                "No fue posible identificar el proceso VRP."
                        }));

                        return;
                    }

                    setVrpProgress({
                        jobId,
                        taxiOSId:
                            response.taxiOSId ||
                            Number(serviceId),
                        status:
                            response.status ||
                            "QUEUED",
                        stage:
                            response.stage ||
                            "QUEUED",
                        progress:
                            Number(response.progress) || 0,
                        message:
                            response.message ||
                            "Solicitud registrada.",
                        manifestsCount: 0,
                        passengersCount:
                            response.passengersCount || 0,
                        error: null
                    });

                    const controller =
                        new AbortController();

                    try {
                        await subscribeTaxiRoutingJob({
                            jobId,
                            signal: controller.signal,

                            onProgress: async update => {
                                setVrpProgress(previous => ({
                                    ...previous,
                                    ...update,
                                    progress: Number(update.progress || 0)
                                }));

                                if (update.status === "COMPLETED") {
                                    await refreshAfterRouting();
                                    showAlert(update.message || "Rutas optimizadas y manifiestos creados correctamente.");
                                }

                                if (update.status === "FAILED") {
                                    showAlert(
                                        update.error ||
                                        update.message ||
                                        "La optimización VRP falló.",
                                        "error"
                                    );
                                }
                            },

                            onError: error => {
                                console.error(
                                    "Error de conexión SSE:",
                                    error
                                );

                                setVrpStreamError(true);
                            },

                            onClose: () => {
                                console.log("Flujo SSE VRP cerrado.");
                            }
                        });
                    } catch (error) {
                        console.error(
                            "No se pudo mantener el SSE:",
                            error
                        );

                        /*
                         * Un fallo de red no significa necesariamente
                         * que el trabajo haya fallado. Consultamos Redis
                         * mediante el endpoint GET.
                         */
                        await recoverVrpJobState(jobId);
                    }
                },

                onError: error => {
                    setVrpProgress({
                        ...initialVrpProgress,
                        status: "FAILED",
                        stage: "FAILED",
                        message:
                            error?.response?.data?.message ||
                            error?.response?.data?.error ||
                            "No fue posible iniciar la optimización.",
                        error:
                            error?.response?.data?.message ||
                            error?.message ||
                            "Error desconocido."
                    });
                }
            }
        );
    };

    const handleOpenInNewWindow = (id) => {
        if (!id) return;
        window.open(`/administracion/servicio2/${id}/viajeros`, "_blank");
    };

    const manifestGroups = Object.values(grouped.manifests).sort((a, b) => {
        if (!a.manid && b.manid) return 1;
        if (a.manid && !b.manid) return -1;
        return Number(a.manid || 0) - Number(b.manid || 0);
    });

    const tableProps = {
        paradas,
        canEdit,
        serviceId,
        user,
        updateTraveler,
        onDelete,
        showAlert,
        refetchTravelers,
        loading,
        isTaxiService: isTaxiServiceType
    };

    // 🎯 ANEW VERSION PARA TAXIS

    const isVrpTerminal =
        vrpProgress.status === "COMPLETED" ||
        vrpProgress.status === "FAILED";

    const isVrpProcessing =
        vrpStarted && !isVrpTerminal;

    const resetVrpState = () => {
        setVrpStarted(false);
        setVrpStreamError(false);
        setVrpProgress(initialVrpProgress);
    };

    const handleCloseVrpDialog = () => {
        /*
         * Se impide cerrar durante el proceso para evitar que
         * el operador piense que fue cancelado.
         */
        if (isVrpProcessing) {
            return;
        }

        setOpenVrpDialog(false);

        window.setTimeout(() => {
            resetVrpState();
        }, 200);
    };

    const recoverVrpJobState = async jobId => {
        try {
            const recoveredJob =
                await getTaxiRoutingJob(jobId);

            setVrpProgress(recoveredJob);

            if (
                recoveredJob.status === "COMPLETED"
            ) {
                await refreshAfterRouting();

                showAlert(
                    recoveredJob.message ||
                    "Rutas y manifiestos generados correctamente."
                );
            }

            if (
                recoveredJob.status === "FAILED"
            ) {
                showAlert(
                    recoveredJob.error ||
                    recoveredJob.message ||
                    "La optimización no pudo completarse.",
                    "error"
                );
            }
        } catch (error) {
            setVrpStreamError(true);

            showAlert(
                error?.response?.data?.message ||
                "Se perdió la conexión con el progreso del trabajo.",
                "error"
            );
        }
    };

    return (
        <Box mb={4}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={900}>{title}</Typography>
                <Chip label={`${travelers.length} pasajeros`} />
            </Stack>

            {manifestGroups.map((group) => {
                const passengersWithTaxi = (group.travelers || []).filter(
                    t => t.ordserdettaxser === true && t.estadoreporte === "VIAJO"
                );

                const hasPassengersRequiringTaxi = passengersWithTaxi.length > 0;
                const manifestTaxiMap = service?.mapa_manifiestos_taxi || {};
                const existingTaxiOrderId = manifestTaxiMap[group.manid] || null;

                return (
                    <Paper key={group.manid || "sin-manifiesto"} elevation={0} sx={{ mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
                        <Box sx={{ p: 2, bgcolor: "primary.50", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <DirectionsBusIcon color="primary" />
                                <Box>
                                    <Typography fontWeight={900}>
                                        {group.manid ? `Manifiesto ${group.manid}` : "Sin manifiesto"}
                                    </Typography>
                                    {service?.tipsernom && (
                                        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 800, display: 'block', mt: 0.2 }}>
                                            📌 {service.tipsernom}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                        {group.maninf || "Unidad no especificada"}
                                    </Typography>
                                </Box>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center">
                                {group.manest && <Chip label={group.manest} size="small" color="primary" />}
                                <Chip label={`${group.travelers.length} pasajeros`} size="small" variant="outlined" />

                                {group.manid && group.manest === "EN_PROCESO" && (
                                    <Tooltip title="Ver mapa de ruta">
                                        <IconButton size="small" color="primary" onClick={() => handleOpenMap(group)}>
                                            <PublicIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {group.manid && group.manest === "COMPLETADO" && !group.whatsappsent && canSendSurveyNotification && (
                                    <Tooltip title="Enviar Encuesta de Satisfacción por WhatsApp">
                                        <span>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                startIcon={
                                                    notifySurveyMutation.isPending && surveyManifestId === group.manid
                                                        ? <CircularProgress size={16} color="inherit" />
                                                        : <WhatsAppIcon />
                                                }
                                                onClick={() => handleOpenSurveyDialog(group.manid)}
                                                disabled={isWorkflowBusy || isSurveyBusy}
                                                sx={{ mr: 1, fontWeight: 700, px: 2 }}
                                            >
                                                {notifySurveyMutation.isPending && surveyManifestId === group.manid
                                                    ? "Enviando..."
                                                    : "Notificar Encuesta"}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}

                                {group.manid && group.manest === "EN_PROCESO" && hasPassengersRequiringTaxi && !isTaxiServiceType && canShowTaxiFunctions && (
                                    existingTaxiOrderId ? (
                                        <Tooltip title={`Ver Orden de Taxi OS-${String(existingTaxiOrderId).padStart(6, "0")} Asociada`}>
                                            <IconButton size="small" color="primary" onClick={() => handleOpenInNewWindow(existingTaxiOrderId)} sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}>
                                                <LaunchIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Iniciar Servicio de Taxi para este Manifiesto">
                                            <IconButton size="small" color="warning" onClick={() => handleOpenConfirmDialog(group.manid, passengersWithTaxi.length)}>
                                                <LocalTaxiIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )
                                )}

                                <Tooltip title={(expandedGroups[group.manid || "sin-manifiesto"] ?? false) ? "Ocultar pasajeros" : "Mostrar pasajeros"}>
                                    <IconButton size="small" onClick={() => handleToggleExpand(group.manid || "sin-manifiesto")}>
                                        {(expandedGroups[group.manid || "sin-manifiesto"] ?? false) ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Box>
                        <Divider />
                        <Collapse in={expandedGroups[group.manid || "sin-manifiesto"] ?? false} timeout="auto" unmountOnExit>
                            <TravelersMiniTable
                                travelers={group.travelers}
                                {...tableProps}
                                permissions={permissions}
                                showSurveyColumn={!!group.whatsappsent}
                                manifestId={group.manid}
                                onRetrySurvey={(perId) => handleRetrySurveyForPassenger(group.manid, perId)}
                                isRetryingSurvey={retrySurveyMutation.isPending}
                                surveyRetryVersion={surveyRetryVersion}
                            />
                        </Collapse>
                    </Paper>
                );
            })}

            {/* SECCIÓN INTERNA MODIFICADA: PASAJEROS QUE NO VIAJARON */}
            {grouped.noTravelers.length > 0 && (
                <Paper
                    elevation={0}
                    sx={{
                        mt: 3,
                        border: "1px solid",
                        borderColor: "error.light",
                        borderRadius: 3,
                        overflow: "hidden",
                    }}
                >
                    <Box sx={{ p: 2, bgcolor: "error.50", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <PersonOffIcon color="error" />
                            <Box>
                                <Typography fontWeight={900}>No viajaron</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Pasajeros registrados en la orden que no abordaron ninguna unidad.
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                            {isTaxiServiceType && (
                                <>
                                    <Tooltip title="Ver todas las ubicaciones de pasajeros en el mapa">
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            startIcon={<PublicIcon />}
                                            onClick={() => setOpenPassengerMap(true)}
                                            sx={{ mr: 1 }}
                                        >
                                            Mapa de pasajeros
                                        </Button>
                                    </Tooltip>

                                    <Tooltip title="Enviar Notificación Masiva de WhatsApp (Preparando Solicitud)">
                                        <span>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                startIcon={notifyInitialMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <WhatsAppIcon />}
                                                onClick={() => setOpenNotifyDialog(true)}
                                                disabled={isWorkflowBusy}
                                                sx={{ mr: 1, fontWeight: 700, px: 2 }}
                                            >
                                                Notificar Preparación
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    {/* Botón para el flujo automático */}
                                    <Tooltip title="Optimización global automática (OR-Tools CVRP)">
                                        <span>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                startIcon={isVrpProcessing ? <CircularProgress size={16} color="inherit" /> : <AutoGraphIcon />}
                                                onClick={handleOpenAutoVrp}
                                                disabled={isWorkflowBusy || isVrpProcessing}
                                                sx={{ mr: 1, fontWeight: 700, px: 2, bgcolor: '#0d47a1', '&:hover': { bgcolor: '#0a3980' } }}
                                            >
                                                {isVrpProcessing ? `${vrpProgress.progress}%` : "Preparar MN Optimizado"}
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    {/* Botón para el flujo iterativo */}
                                    <Tooltip title="Optimización iterativa por grupos de capacidad (selección manual)">
                                        <span>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                startIcon={<AutoGraphIcon />}
                                                onClick={handleOpenIterative}
                                                disabled={isWorkflowBusy || isVrpProcessing}
                                                sx={{ mr: 1, fontWeight: 700, px: 2, bgcolor: '#0d47a1', '&:hover': { bgcolor: '#0a3980' } }}
                                            >
                                                Preparar MN Iterativo
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </>
                            )}

                            <Chip label={`${grouped.noTravelers.length} pasajeros`} color="error" size="small" />
                            <Tooltip title={(expandedGroups["no-viajaron"] ?? false) ? "Ocultar pasajeros" : "Mostrar pasajeros"}>
                                <IconButton size="small" onClick={() => handleToggleExpand("no-viajaron")}>
                                    {(expandedGroups["no-viajaron"] ?? false) ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>
                    <Divider />
                    <Collapse in={expandedGroups["no-viajaron"] ?? false} timeout="auto" unmountOnExit>
                        <TravelersMiniTable travelers={grouped.noTravelers} {...tableProps} permissions={permissions} />
                    </Collapse>
                </Paper>
            )}

            {/* MODAL DE CONFIRMACIÓN DE INICIALIZACIÓN DE OS DE TAXI */}
            <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalTaxiIcon color="warning" /> Confirmar Inicialización de Taxi
                </DialogTitle>
                <DialogContent dividers>
                    {!generatedTaxiOSId ? (
                        <DialogContentText>
                            Está a punto de aperturar el servicio de transbordo a taxi para el <b>Manifiesto {selectedManifestData?.manId}</b>.
                            <br /><br />
                            Esto creará automáticamente una nueva <b>Orden de Servicio Hija (Tipo Pasajero)</b> y migrará a los <b>{selectedManifestData?.totalPassengers} pasajeros</b> que confirmaron su asistencia en el autobús principal.
                            <br /><br />
                            ¿Desea continuar con la inicialización?
                        </DialogContentText>
                    ) : (
                        <Box sx={{ p: 1, textAlign: 'center' }}>
                            <Alert severity="success" sx={{ mb: 2, fontWeight: 700 }}>¡Orden de servicio generada con éxito!</Alert>
                            <Typography variant="body1" mb={2}>
                                Se ha creado el registro maestro <b>OS-0000${generatedTaxiOSId}</b> para el despacho de las unidades de taxi.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    {!generatedTaxiOSId ? (
                        <>
                            <Button onClick={handleCloseConfirmDialog} color="secondary" disabled={isWorkflowBusy}>Cancelar</Button>
                            <Button onClick={handleExecuteTaxiInit} variant="contained" color="warning" disabled={isWorkflowBusy} startIcon={isWorkflowBusy ? <CircularProgress size={20} color="inherit" /> : null}>
                                {isWorkflowBusy ? "Procesando..." : "Sí, Inicializar"}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={handleCloseConfirmDialog} color="default">Volver al tablero</Button>
                            <Button variant="contained" color="primary" startIcon={<LaunchIcon />} onClick={() => { handleOpenInNewWindow(generatedTaxiOSId); handleCloseConfirmDialog(); }}>
                                Abrir Orden en nueva pestaña
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>



            {/* MODAL DE CONFIRMACIÓN DE ENVÍO DE WHATSAPP (ETAPA 2) */}
            <Dialog open={openNotifyDialog} onClose={() => !isWorkflowBusy && setOpenNotifyDialog(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WhatsAppIcon color="success" /> Despacho Masivo de Mensajes
                </DialogTitle>
                <DialogContent dividers>
                    <DialogContentText>
                        ¿Está seguro que desea enviar la <b>Notificación Inicial de Preparación</b> a todos los pasajeros de esta Orden de Taxi?
                        <br /><br />
                        Esto registrará las alertas correspondientes e informará a los usuarios que su solicitud de transbordo de última milla está siendo procesada.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenNotifyDialog(false)} color="secondary" disabled={isWorkflowBusy}>
                        Cancelar
                    </Button>
                    <Button onClick={handleExecuteInitialNotification} variant="contained" color="success" disabled={isWorkflowBusy}>
                        {isWorkflowBusy ? "Enviando..." : "Sí, Notificar"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openAutoVrpDialog}
                onClose={handleCloseAutoVrp}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown={isVrpProcessing}
            >
                <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}
                >
                    <AutoGraphIcon color="primary" />

                    {!vrpStarted
                        ? "Optimización de rutas"
                        : "Procesando rutas de taxi"}
                </DialogTitle>

                <DialogContent dividers>
                    {!vrpStarted ? (
                        <DialogContentText>
                            Está a punto de ejecutar el optimizador global de rutas basado en{" "} <b>OR-Tools CVRP</b>.
                            <br /><br />
                            El sistema calculará la matriz de tiempos con Mapbox, agrupará a los pasajeros respetando la capacidad de cada taxi, optimizará el orden de las paradas y creará los manifiestos correspondientes.
                            <br /><br />
                            Durante el procesamiento la pantalla permanecerá bloqueada para evitar operaciones duplicadas.
                            <br /><br />
                            ¿Desea iniciar la optimización?
                        </DialogContentText>
                    ) : (
                        <Stack spacing={2.5}>
                            {vrpProgress.status === "FAILED" && (
                                <Alert severity="error">
                                    {vrpProgress.error ||
                                        vrpProgress.message ||
                                        "La optimización no pudo completarse."}
                                </Alert>
                            )}

                            {vrpProgress.status ===
                                "COMPLETED" && (
                                    <Alert severity="success">
                                        {vrpProgress.message ||
                                            "Rutas y manifiestos creados correctamente."}
                                    </Alert>
                                )}

                            {vrpStreamError &&
                                !isVrpTerminal && (
                                    <Alert severity="warning">
                                        Se interrumpió temporalmente la conexión de progreso. El proceso puede continuar ejecutándose en el servidor.
                                    </Alert>
                                )}

                            <Box>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={1}
                                >
                                    <Typography
                                        variant="body2"
                                        fontWeight={800}
                                    >
                                        {vrpProgress.message ||
                                            "Procesando solicitud..."}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        fontWeight={900}
                                        color="primary.main"
                                    >
                                        {Math.max(
                                            0,
                                            Math.min(
                                                100,
                                                Number(
                                                    vrpProgress.progress ||
                                                    0
                                                )
                                            )
                                        )}
                                        %
                                    </Typography>
                                </Stack>

                                <LinearProgress
                                    variant="determinate"
                                    value={Math.max(
                                        0,
                                        Math.min(
                                            100,
                                            Number(
                                                vrpProgress.progress ||
                                                0
                                            )
                                        )
                                    )}
                                    sx={{
                                        height: 12,
                                        borderRadius: 6
                                    }}
                                />
                            </Box>

                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    bgcolor: "grey.50"
                                }}
                            >
                                <Stack spacing={0.8}>
                                    <Typography variant="body2">
                                        <b>Estado:</b>{" "}
                                        {vrpProgress.status}
                                    </Typography>

                                    <Typography variant="body2">
                                        <b>Etapa:</b>{" "}
                                        {vrpProgress.stage}
                                    </Typography>

                                    {vrpProgress.passengersCount >
                                        0 && (
                                            <Typography variant="body2">
                                                <b>Pasajeros:</b>{" "}
                                                {
                                                    vrpProgress.passengersCount
                                                }
                                            </Typography>
                                        )}

                                    {vrpProgress.manifestsCount >
                                        0 && (
                                            <Typography variant="body2">
                                                <b>Manifiestos:</b>{" "}
                                                {
                                                    vrpProgress.manifestsCount
                                                }
                                            </Typography>
                                        )}

                                    {vrpProgress.jobId && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                wordBreak:
                                                    "break-all"
                                            }}
                                        >
                                            Trabajo:{" "}
                                            {vrpProgress.jobId}
                                        </Typography>
                                    )}
                                </Stack>
                            </Paper>

                            {isVrpProcessing && (
                                <Alert severity="info">
                                    No cierre ni recargue esta página durante la creación de los manifiestos.
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    {!vrpStarted ? (
                        <>
                            <Button
                                onClick={handleCloseAutoVrp}
                                color="secondary"
                                disabled={
                                    generateRoutesMutation.isPending
                                }
                            >
                                Cancelar
                            </Button>

                            <Button
                                onClick={
                                    handleExecuteVrpOptimization
                                }
                                variant="contained"
                                color="primary"
                                disabled={generateRoutesMutation.isPending}
                                startIcon={generateRoutesMutation.isPending ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <AutoGraphIcon />
                                )
                                }
                            >
                                {generateRoutesMutation.isPending
                                    ? "Registrando..."
                                    : "Iniciar optimización"}
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={handleCloseAutoVrp}
                            variant="contained"
                            disabled={isVrpProcessing}
                        >
                            {isVrpProcessing
                                ? "Procesando..."
                                : "Cerrar"}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <VrpIterativeDialog
                open={openIterativeDialog}
                onClose={handleCloseIterative}
                taxiOSId={Number(serviceId)}
                origin={{
                    lat: service?.taxi_punto_transbordo_lat,
                    lng: service?.taxi_punto_transbordo_lng,
                    nom: service?.taxi_punto_transbordo_nom || "Origen"
                }}
                paradas={paradas}
                onPersistSuccess={handleVrpPersistSuccess}
                onPersistError={handleVrpPersistError}
            />

            {/* Diálogo de Mapas */}
            <Dialog open={openMap} onClose={handleCloseMap} fullWidth maxWidth="lg" sx={{ "& .MuiDialog-paper": { height: "80vh" } }}>
                <DialogTitle>Mapa de Ruta</DialogTitle>
                <DialogContent>
                    {mapData && <RouteMap key={mapData.id} route={mapData.id} vehid={mapData.vehicleId} />}
                </DialogContent>
            </Dialog>

            <Dialog open={openPassengerMap} onClose={() => setOpenPassengerMap(false)} fullWidth maxWidth="lg" sx={{ '& .MuiDialog-paper': { height: '80vh' } }}>
                <DialogTitle>Ubicaciones de pasajeros</DialogTitle>
                <DialogContent>
                    <PassengerLocationMap travelers={travelers} paradas={paradas} origenid={service.taxi_punto_transbordo_id} />
                </DialogContent>
            </Dialog>

            {/* MODAL DE CONFIRMACIÓN DE ENVÍO DE ENCUESTA DE SATISFACCIÓN */}
            <Dialog
                open={openSurveyDialog}
                onClose={handleCloseSurveyDialog}
                fullWidth
                maxWidth="xs"
                disableEscapeKeyDown={notifySurveyMutation.isPending}
            >
                <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
                    <WhatsAppIcon color="success" /> Encuesta de Satisfacción
                </DialogTitle>

                <DialogContent dividers>
                    <DialogContentText>
                        ¿Desea enviar la <b>Encuesta de Satisfacción</b> a todos los pasajeros
                        del <b>Manifiesto {surveyManifestId}</b>?
                        <br /><br />
                        Cada pasajero recibirá un mensaje de WhatsApp con un enlace
                        personalizado a la encuesta.
                        <br /><br />
                        Una vez enviada, el manifiesto quedará marcado y no podrá reenviarse
                        desde este botón.
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={handleCloseSurveyDialog}
                        color="secondary"
                        disabled={notifySurveyMutation.isPending}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleExecuteSurveyNotification}
                        variant="contained"
                        color="success"
                        disabled={notifySurveyMutation.isPending}
                        startIcon={
                            notifySurveyMutation.isPending
                                ? <CircularProgress size={18} color="inherit" />
                                : <WhatsAppIcon />
                        }
                    >
                        {notifySurveyMutation.isPending ? "Enviando..." : "Sí, Enviar Encuesta"}
                    </Button>
                </DialogActions>
            </Dialog>

            {isVrpProcessing && (
                <Box
                    sx={{
                        position: "fixed",
                        inset: 0,
                        zIndex: theme =>
                            theme.zIndex.modal - 1,
                        bgcolor: "rgba(0, 0, 0, 0.35)",
                        backdropFilter: "blur(2px)",
                        pointerEvents: "all"
                    }}
                />
            )}
        </Box>
    );
};

export default TravelersManifestReport;